/**
 * Pantalla de partida: junta las tres zonas, la mano y el estado del turno.
 *
 * La responsividad es el punto delicado, y se resuelve con una idea concreta:
 * **el tablero no cambia de forma entre móvil y escritorio, solo de tamaño.**
 * Tres columnas siempre, la fila del rival arriba y la tuya abajo. Aprenderlo en
 * el móvil sirve para el escritorio y al revés, y no hay dos maquetaciones que
 * mantener.
 *
 * Lo que sí cambia es de dónde viene el límite de tamaño. En vertical manda el
 * ancho: las tres columnas ocupan la pantalla. En escritorio manda el alto —
 * dejar que el tablero crezca a lo ancho lo sacaría por debajo del pliegue — así
 * que el ancho máximo se deriva del alto disponible. De ahí el `calc` del
 * contenedor: es la relación entre ancho y alto del tablero (cuatro filas de
 * cartas 5:7 más el marcador) despejada al revés.
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ChevronsRight,
  CircleHelp,
  Layers,
  Lightbulb,
  RotateCcw,
  Swords,
  Trophy,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { CardDetail } from '@/components/cards/CardDetail';
import { HandOverlay } from '@/components/cards/HandOverlay';
import { Button } from '@/components/ui/Button';
import type { DeckEntry } from '@/engine/combatEngine';
import { computeTotalPower } from '@/engine/effects';
import { useGameState } from '@/hooks/useGameState';
import { RULES } from '@/types/game';
import { computeCardPower } from '@/engine/effects';
import type { BoardCard, CardInstance, MatchResult, ZoneIndex } from '@/types/game';
import { FirstMatchGuide } from './FirstMatchGuide';
import { TurnTimer } from './TurnTimer';
import { ZoneComponent } from './ZoneComponent';

/**
 * Ancho máximo del tablero, derivado del alto libre y del número de filas.
 *
 * En escritorio manda el alto: si se dejara crecer a lo ancho, el tablero se
 * saldría por debajo del pliegue. Así que se despeja al revés — cuántas filas de
 * cartas hay que apilar y cuánto alto queda — y sale el ancho.
 *
 * La cuenta: cada zona ocupa un tercio del ancho y mete 2 cartas por fila, luego
 * una carta mide W/6; con proporción 5:7 su alto es 1,4 veces su ancho. Se deriva
 * del ruleset y no de un número escrito a mano, así que cambiar de partida de 6
 * a 10 turnos (4 filas frente a 6) reajusta el tablero solo.
 *
 * El max(300px, ...) es la red de seguridad: en una ventana muy baja la resta se
 * iría a negativo y el tablero colapsaría a nada.
 */
const CARD_ROWS = Math.ceil(RULES.cardsPerZone / 2) * 2;
const WIDTH_PER_HEIGHT = (6 / (CARD_ROWS * 1.4)).toFixed(2);
const BOARD_MAX_WIDTH = `min(1024px, max(300px, calc((100dvh - 290px) * ${WIDTH_PER_HEIGHT})))`;

function ResultOverlay({ result, onRestart }: { result: MatchResult; onRestart: () => void }) {
  const won = result.winner === 'PLAYER';
  const draw = result.winner === 'DRAW';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 z-40 grid place-items-center bg-slate-950/85 p-4 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.85, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        className="w-full max-w-sm rounded-2xl border border-white/15 bg-slate-900 p-6 text-center shadow-2xl"
      >
        <Trophy
          className={`mx-auto h-12 w-12 ${
            draw ? 'text-slate-400' : won ? 'text-amber-400' : 'text-rose-400'
          }`}
          aria-hidden
        />
        <h2 className="mt-3 text-2xl font-black text-white">
          {draw ? 'Empate' : won ? '¡Victoria!' : 'Derrota'}
        </h2>
        <p className="mt-1 text-sm text-slate-400">{result.reason}</p>

        <dl className="mt-4 grid grid-cols-2 gap-2 text-sm">
          <div className="rounded-lg bg-slate-800/70 p-2">
            <dt className="text-[10px] tracking-wider text-slate-400 uppercase">Zonas</dt>
            <dd className="font-bold tabular-nums text-white">
              {result.zonesWon.PLAYER} — {result.zonesWon.OPPONENT}
            </dd>
          </div>
          <div className="rounded-lg bg-slate-800/70 p-2">
            <dt className="text-[10px] tracking-wider text-slate-400 uppercase">Poder total</dt>
            <dd className="font-bold tabular-nums text-white">
              {result.totalPower.PLAYER} — {result.totalPower.OPPONENT}
            </dd>
          </div>
        </dl>

        <Button size="lg" className="mt-5 w-full" onClick={onRestart}>
          <RotateCcw className="h-4 w-4" aria-hidden />
          Jugar otra vez
        </Button>
      </motion.div>
    </motion.div>
  );
}

interface GameBoardProps {
  /** Mazo del jugador, con nivel si alguna carta está fusionada. */
  deckMasterIds?: readonly DeckEntry[];
}

export function GameBoard({ deckMasterIds }: GameBoardProps = {}) {
  const {
    game,
    player,
    opponent,
    zoneScores,
    validZones,
    pendingCardId,
    pendingCardIssue,
    selectedCardId,
    notice,
    isResolving,
    highlightedCardId,
    revealMessage,
    suggestion,
    hintVisible,
    guideDismissed,
    dismissGuide,
    inspectedCardId,
    inspectCard,
    toggleHint,
    newMatch,
    selectCard,
    setDragging,
    playSelectedInto,
    playCardInto,
    recall,
    endTurn,
  } = useGameState();

  // Reabierta a mano desde el boton de ayuda. La apertura automatica de la
  // primera vez la decide el store.
  const [guideReopened, setGuideReopened] = useState(false);
  const guideOpen = guideReopened || !guideDismissed;

  // Reparte sola al abrir: se entra jugando, no eligiendo en un menú. El guard
  // sobre `game` hace que volver a ejecutarse (por ejemplo al resolverse el mazo)
  // no interrumpa una partida en curso.
  useEffect(() => {
    if (!game) newMatch({ deckIds: deckMasterIds });
  }, [game, newMatch, deckMasterIds]);

  if (!game || !player || !opponent) {
    return <div className="grid flex-1 place-items-center text-slate-400">Repartiendo…</div>;
  }

  const finished = game.phase === 'FINISHED';
  const opponentPower = computeTotalPower(game, 'OPPONENT');
  const playerPower = computeTotalPower(game, 'PLAYER');

  const handleRecall = (card: BoardCard) => recall(card.instanceId);

  /**
   * Carta que se está mirando ampliada.
   *
   * Puede estar en la mano o en el tablero; se busca en los dos sitios porque el
   * gesto es el mismo esté donde esté, y el jugador no debería tener que saber
   * dónde vive el dato.
   */
  const inspected: { card: CardInstance; power?: number } | null = (() => {
    if (!inspectedCardId) return null;
    const inHand = player.hand.find((item) => item.instanceId === inspectedCardId);
    if (inHand) return { card: inHand };
    for (const zone of game.zones) {
      const onBoard = zone.cards.find((item) => item.instanceId === inspectedCardId);
      if (onBoard) return { card: onBoard, power: computeCardPower(zone, onBoard) };
    }
    return null;
  })();

  const handleZoneClick = (zone: ZoneIndex) => {
    if (!pendingCardId || isResolving) return;
    playSelectedInto(zone);
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {/* Estado del rival y del turno */}
      <div className="flex shrink-0 items-center justify-between gap-2 px-2 py-1.5 sm:px-4">
        <div className="flex min-w-0 items-center gap-2">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-rose-900/70 text-xs font-bold text-rose-200 ring-1 ring-rose-400/40 sm:h-9 sm:w-9 sm:text-sm">
            {opponent.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0 leading-tight">
            <p className="truncate text-xs font-semibold text-slate-200 sm:text-sm">
              {opponent.name}
            </p>
            <p className="flex items-center gap-2 text-[10px] text-slate-400 sm:text-xs">
              <span className="inline-flex items-center gap-0.5">
                <Layers className="h-3 w-3" aria-hidden />
                {opponent.hand.length}
              </span>
              <span className="inline-flex items-center gap-0.5 tabular-nums">
                <Swords className="h-3 w-3" aria-hidden />
                {opponentPower}
              </span>
            </p>
          </div>
        </div>

        <TurnTimer
          turn={game.turn}
          maxTurns={game.maxTurns}
          energy={player.energy}
          maxEnergy={player.maxEnergy}
        />

        <div className="flex items-center gap-2">
          <span className="hidden text-xs tabular-nums text-slate-400 sm:inline">
            Tu poder: <strong className="text-slate-100">{playerPower}</strong>
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setGuideReopened(true)}
            title="Cómo se juega"
            aria-label="Cómo se juega"
          >
            <CircleHelp className="h-4 w-4" aria-hidden />
          </Button>

          {/* El consejo es opt-in: aparecer solo convertiría el ayudante en
              ruido y le quitaría al jugador la decisión, que es el juego. */}
          <Button
            size="sm"
            variant={hintVisible ? 'primary' : 'ghost'}
            onClick={toggleHint}
            disabled={isResolving || finished}
            title="Pedir consejo"
            aria-label="Pedir consejo"
          >
            <Lightbulb className="h-4 w-4" aria-hidden />
          </Button>
          {/* En móvil solo el icono. Los dos botones nuevos (ayuda y consejo)
              se comieron los 19 px de holgura que tenía esta barra en una
              pantalla de 360, y el texto era lo único prescindible: la flecha
              junto al contador de turno se entiende sin leerla. */}
          <Button
            size="sm"
            onClick={() => void endTurn()}
            disabled={isResolving || finished}
            // Dinámico: con un aria-label fijo, un lector de pantalla seguiría
            // diciendo "terminar turno" mientras la partida se revela.
            aria-label={isResolving ? 'Revelando' : 'Terminar turno'}
            title={isResolving ? 'Revelando' : 'Terminar turno'}
          >
            <ChevronsRight className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">
              {isResolving ? 'Revelando…' : 'Terminar turno'}
            </span>
          </Button>
        </div>
      </div>

      {/* Tablero */}
      {/* overscroll-contain: si la pantalla es tan baja que el tablero no cabe,
          se puede desplazar sin que el gesto se propague y arrastre la página. */}
      <div className="flex min-h-0 flex-1 justify-center overflow-y-auto overscroll-contain px-1 sm:px-4">
        <div
          className="grid h-fit w-full grid-cols-3 gap-1 sm:gap-3"
          style={{ maxWidth: BOARD_MAX_WIDTH }}
        >
          {game.zones.map((zone) => {
            const score = zoneScores[zone.index];
            if (!score) return null;
            return (
              <ZoneComponent
                // La identidad de una zona es su posición en el tablero, no la
                // ubicación que le haya tocado: dos zonas podrían compartir
                // ubicación y React vería claves duplicadas.
                key={zone.index}
                zone={zone}
                score={score}
                turn={game.turn}
                highlightedCardId={highlightedCardId}
                hasPendingCard={Boolean(pendingCardId)}
                isValidTarget={validZones[zone.index]}
                suggested={suggestion?.zone === zone.index}
                onSelectZone={() => handleZoneClick(zone.index)}
                onRecall={handleRecall}
                onInspect={(card) => inspectCard(card.instanceId)}
              />
            );
          })}
        </div>
      </div>

      {/* Franja de mensajes. Altura fija: si apareciera y desapareciera, el
          tablero daría un salto justo en mitad de la revelación. */}
      <div className="flex h-7 shrink-0 items-center justify-center px-3 sm:h-8">
        <AnimatePresence mode="wait">
          {notice ? (
            <motion.p
              key={`notice-${notice}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="inline-flex items-center gap-1.5 rounded-full bg-rose-950/80 px-3 py-1 text-[11px] font-medium text-rose-200 ring-1 ring-rose-500/40 sm:text-xs"
            >
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {notice}
            </motion.p>
          ) : suggestion ? (
            <motion.p
              key={`hint-${suggestion.instanceId}-${suggestion.zone}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="inline-flex items-center gap-1.5 truncate rounded-full bg-emerald-950/80 px-3 py-1 text-[11px] font-medium text-emerald-100 ring-1 ring-emerald-400/40 sm:text-xs"
            >
              <Lightbulb className="h-3 w-3 shrink-0" aria-hidden />
              {suggestion.reason}
            </motion.p>
          ) : hintVisible ? (
            <motion.p
              key="hint-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[11px] text-slate-500"
            >
              No hay ninguna jugada que merezca la pena. Termina el turno.
            </motion.p>
          ) : pendingCardIssue ? (
            // Persistente mientras la carta siga elegida, al contrario que el
            // aviso: el jugador tiene que poder leerlo con calma y decidir otra
            // cosa, no verlo desaparecer a los dos segundos.
            <motion.p
              key={`issue-${pendingCardIssue}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-950/80 px-3 py-1 text-[11px] font-medium text-amber-100 ring-1 ring-amber-500/40 sm:text-xs"
            >
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {pendingCardIssue}
            </motion.p>
          ) : revealMessage ? (
            <motion.p
              key={`reveal-${revealMessage}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="truncate rounded-full bg-slate-800/80 px-3 py-1 text-[11px] font-medium text-amber-100 ring-1 ring-amber-400/30 sm:text-xs"
            >
              {revealMessage}
            </motion.p>
          ) : (
            <motion.p
              key="hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[10px] text-slate-500 sm:text-xs"
            >
              {pendingCardId
                ? 'Toca una zona para colocar la carta'
                : 'Arrastra una carta a una zona · mantén pulsado para verla'}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      <HandOverlay
        cards={player.hand}
        energy={player.energy}
        deckCount={player.deck.length}
        disabled={isResolving || finished}
        selectedCardId={selectedCardId}
        onSelect={selectCard}
        onDropIntoZone={playCardInto}
        onDragStateChange={setDragging}
        onInspect={inspectCard}
        suggestedCardId={suggestion?.instanceId ?? null}
      />

      <FirstMatchGuide
        open={guideOpen}
        onClose={() => {
          setGuideReopened(false);
          dismissGuide();
        }}
      />

      <AnimatePresence>
        {inspected ? (
          <CardDetail
            card={inspected.card}
            currentPower={inspected.power}
            onClose={() => inspectCard(null)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {finished && game.result ? (
          <ResultOverlay
            result={game.result}
            onRestart={() => newMatch({ deckIds: deckMasterIds })}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
